import { useState } from 'react';
import type { EnrichedFeature } from '@/hooks/useGeoLayer';
import { StarRating } from './StarRating';
import styles from './SiteListPanel.module.css';

interface Props {
  features: EnrichedFeature[];
  selectedId: string | number | null;
  onSelect: (feature: EnrichedFeature) => void;
  layerId?: string;
  className?: string;
  variant?: 'default' | 'agritour';
}

const BADGE_LABELS: Record<string, string> = { active: 'פעיל', lead: 'ליד', inactive: 'לא פעיל' };
const BADGE_CLASS: Record<string, string> = {
  active: styles.badgeActive,
  lead: styles.badgeLead,
  inactive: styles.badgeInactive,
};

const GEOMETRY_LABELS: Record<string, string> = {
  point: 'נקודה',
  polygon_drawn: 'מצויר',
};

function attrString(attrs: Record<string, unknown>, key: string): string | null {
  const val = attrs[key];
  if (val === null || val === undefined) return null;
  const text = String(val).trim();
  return text.length > 0 ? text : null;
}

export function SiteListPanel({
  features,
  selectedId,
  onSelect,
  layerId,
  className,
  variant = 'default',
}: Props) {
  const isAgritour = variant === 'agritour' || layerId === 'agritour-entrepreneurs';
  const isEntrepreneurs =
    layerId === 'entrepreneurs' || layerId === 'agritour-entrepreneurs';
  const panelTitle =
    layerId === 'agritour-entrepreneurs'
      ? 'בקשות'
      : isEntrepreneurs
        ? 'יזמים'
        : 'אתרים';
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = features.filter((f) => {
    const q = search.toLowerCase();
    const matchSearch = !q || f.name.includes(search) || f.crm.manager.includes(search);
    const matchStatus = statusFilter === 'all' || f.crm.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div
      className={`${styles.panel}${isAgritour ? ` ${styles.panelAgritour}` : ''}${className ? ` ${className}` : ''}`}
    >
      <div className={styles.panelHeader}>
        <p className={styles.panelTitle}>{panelTitle}</p>
        <input
          className={styles.searchInput}
          placeholder={isAgritour ? 'חיפוש לפי שם מיזם / מגיש...' : 'חיפוש לפי שם / מנהל...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          dir="rtl"
        />
        {!isAgritour ? (
          <select
            className={styles.statusSelect}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">כל הסטטוסים</option>
            <option value="active">פעיל</option>
            <option value="lead">ליד</option>
            <option value="inactive">לא פעיל</option>
          </select>
        ) : null}
      </div>

      <div className={styles.count}>{filtered.length} תוצאות</div>

      <div className={styles.items}>
        {filtered.map((f) => (
          <button
            key={f.id}
            data-testid={`list-item-${f.id}`}
            aria-pressed={selectedId === f.id}
            className={`${styles.item}${selectedId === f.id ? ` ${styles.selected}` : ''}`}
            onClick={() => onSelect(f)}
          >
            <div className={styles.itemHeader}>
              <span className={styles.itemName}>{f.name}</span>
              {isAgritour ? (
                <span className={styles.badgeAgritour}>
                  {GEOMETRY_LABELS[String(f.attributes.selection_mode ?? '')] ?? 'מיקום'}
                </span>
              ) : (
                <span className={`${styles.badge} ${BADGE_CLASS[f.crm.status] ?? ''}`}>
                  {BADGE_LABELS[f.crm.status]}
                </span>
              )}
            </div>
            <div className={styles.itemMeta}>
              {isAgritour ? (
                <>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>מגיש</span>
                    {f.crm.manager}
                  </span>
                  {attrString(f.attributes, 'settlement') ? (
                    <span className={styles.metaItem}>
                      <span className={styles.metaLabel}>יישוב</span>
                      {attrString(f.attributes, 'settlement')}
                    </span>
                  ) : null}
                </>
              ) : isEntrepreneurs ? (
                <>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>מגרש</span>
                    {String(f.attributes.migrash ?? '—')}
                  </span>
                  {f.attributes.designation ? (
                    <span className={styles.metaItem}>
                      <span className={styles.metaLabel}>ייעוד</span>
                      {String(f.attributes.designation)}
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>מנהל</span>
                    {f.crm.manager}
                  </span>
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>חדרים</span>
                    {f.crm.rooms}
                  </span>
                  <StarRating stars={f.crm.stars} />
                </>
              )}
            </div>
            {!f.geometry && <div className={styles.noGeo}>אין מיקום גאוגרפי</div>}
          </button>
        ))}

        {filtered.length === 0 && (
          <div className={styles.empty}>{isAgritour ? 'לא נמצאו בקשות' : 'לא נמצאו אתרים'}</div>
        )}
      </div>
    </div>
  );
}
